// Calendar event types
export const CALENDAR_EVENT_TYPES = [
  'holiday',
  'sick',
  'activity',
  'reminder',
  'closure',
  'early_pickup',
  'late_start',
] as const
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number]

// Parent-only event types (absences)
export const PARENT_EVENT_TYPES = ['holiday', 'sick'] as const
export type ParentEventType = (typeof PARENT_EVENT_TYPES)[number]

// Childminder-only event types
export const CHILDMINDER_EVENT_TYPES = [
  'activity',
  'reminder',
  'closure',
  'early_pickup',
  'late_start',
] as const
export type ChildminderEventType = (typeof CHILDMINDER_EVENT_TYPES)[number]

// Calendar event
export interface CalendarEvent {
  id: string
  type: CalendarEventType
  title: string
  notes: string | null
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  appliesToAll: boolean
  createdByRole: 'childminder' | 'parent'
  createdByUserId: string
  childminderId: string
  children: CalendarEventChild[]
  createdAt: string
  updatedAt: string
}

export interface CalendarEventChild {
  id: string
  firstName: string
  lastName: string
}

// Attendance types
export type AttendanceStatus = 'expected' | 'checked_in' | 'checked_out' | 'absent'

export interface AttendanceChild {
  id: string
  firstName: string
  lastName: string
  status: AttendanceStatus
  absenceReason?: 'holiday' | 'sick'
  checkedInAt?: string
  checkedOutAt?: string
  notes?: string
}

export interface AttendanceSummary {
  date: string
  expectedChildren: AttendanceChild[]
  summary: {
    expected: number
    checkedIn: number
    checkedOut: number
    absent: number
  }
}

// API request/response types
export interface CreateCalendarEventRequest {
  type: ChildminderEventType
  title?: string
  notes?: string
  startDate: string
  endDate: string
  childIds?: string[]
  appliesToAll?: boolean
}

export interface CreateAbsenceRequest {
  type: ParentEventType
  startDate: string
  endDate: string
  childIds: string[]
  notes?: string
}

export interface UpdateCalendarEventRequest {
  title?: string
  notes?: string | null
  startDate?: string
  endDate?: string
  childIds?: string[]
  appliesToAll?: boolean
}

export interface UpdateAbsenceRequest {
  startDate?: string
  endDate?: string
  notes?: string | null
}

export interface ListEventsParams {
  startDate?: string
  endDate?: string
  childId?: string
}

// Helper functions
export function getEventTypeLabel(type: CalendarEventType): string {
  const labels: Record<CalendarEventType, string> = {
    holiday: 'Holiday',
    sick: 'Sick Day',
    activity: 'Activity',
    reminder: 'Reminder',
    closure: 'Closure',
    early_pickup: 'Early Pickup',
    late_start: 'Late Start',
  }
  return labels[type]
}

export function getEventTypeColor(type: CalendarEventType): string {
  const colors: Record<CalendarEventType, string> = {
    holiday: 'bg-blue-500',
    sick: 'bg-red-500',
    activity: 'bg-green-500',
    reminder: 'bg-yellow-500',
    closure: 'bg-gray-500',
    early_pickup: 'bg-orange-500',
    late_start: 'bg-purple-500',
  }
  return colors[type]
}

export function getEventTypeDotColor(type: CalendarEventType): string {
  const colors: Record<CalendarEventType, string> = {
    holiday: 'bg-blue-400',
    sick: 'bg-red-400',
    activity: 'bg-green-400',
    reminder: 'bg-yellow-400',
    closure: 'bg-gray-400',
    early_pickup: 'bg-orange-400',
    late_start: 'bg-purple-400',
  }
  return colors[type]
}

export function isParentEventType(type: CalendarEventType): type is ParentEventType {
  return PARENT_EVENT_TYPES.includes(type as ParentEventType)
}

export function isChildminderEventType(type: CalendarEventType): type is ChildminderEventType {
  return CHILDMINDER_EVENT_TYPES.includes(type as ChildminderEventType)
}
